import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface ParagraphAudioDto {
  jobId: string;
  url: string;
  durationMs?: number;
  createdAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Returns completed audio generation results for a given paragraph.
 * Filters the generation history for audio-type completions that have a URL.
 */
export function useParagraphAudio(paragraphId: string | null | undefined) {
  const { getToken } = useAuth();
  return useQuery<ParagraphAudioDto[]>({
    enabled: !!paragraphId,
    queryKey: ['paragraph-audio', paragraphId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const res = await client
        .get(`api/v2/paragraphs/${paragraphId}/generations`)
        .json<
          ApiResponse<
            {
              jobId: string;
              slug: string;
              status: string;
              createdAt: string;
              result?: Record<string, unknown> | null;
            }[]
          >
        >();
      return (res.data ?? [])
        .filter(
          (g) =>
            (g.slug === 'audio-generation' ||
              g.slug.includes('audio') ||
              g.slug.includes('narrat') ||
              g.slug.includes('voice')) &&
            g.status === 'complete' &&
            (g.result as Record<string, unknown> | null)?.url,
        )
        .map((g) => {
          const r = g.result as { url: string; duration_ms?: number };
          return {
            jobId: g.jobId,
            url: r.url,
            durationMs: r.duration_ms,
            createdAt: g.createdAt,
          };
        });
    },
  });
}
